import { loadStaticGlb } from "../assets/gltf.js";
import {
  WORLD_DROP_CELL_SIZE,
  getInteractionProfile,
  getItemDefinition,
  type DroppedItemEntity,
  type ItemWorldAssetId,
} from "@endclock/shared";
import type { Camera } from "../engine/camera.js";
import type { MeshGeometry } from "../engine/mesh.js";
import {
  type GpuMesh,
  type Renderer,
  type StaticRenderMaterial,
} from "../engine/renderer.js";
import type { InteractionCandidate } from "../interaction/target-selection.js";
import { ReusableInstanceBatch, type StaticInstanceObject } from "../world/static-instances.js";
import type { ClientInventorySnapshot } from "./inventory-store.js";

export const WORLD_DROP_QUERY_RADIUS = 4;
export const WORLD_DROP_QUERY_RADIUS_MAXIMUM = 8;
export const WORLD_DROP_QUERY_CHECK_LIMIT = 128;

type DropVisualId = ItemWorldAssetId;

interface DropVisualSource {
  readonly geometry: MeshGeometry;
  readonly color: readonly [number, number, number, number];
  readonly centerY: number;
  readonly boundsRadius: number;
  readonly interactionRadius: number;
}

interface LoadedDropVisual extends DropVisualSource {
  readonly mesh: GpuMesh;
  readonly batch: ReusableInstanceBatch;
}

interface PreparedDrop extends StaticInstanceObject {
  readonly entity: DroppedItemEntity;
  readonly visualId: DropVisualId;
}

export interface WorldDropWorldSnapshot {
  readonly entityCount: number;
  readonly visibleCount: number;
  readonly drawCalls: number;
  readonly bucketCount: number;
  readonly lastQueryBuckets: number;
  readonly lastQueryEntityChecks: number;
  readonly lastQueryTruncated: boolean;
}

const MATERIAL: StaticRenderMaterial = Object.freeze({ alphaCutoff: 0, doubleSided: false });
const DROPPED_ITEM_PROFILE = getInteractionProfile("dropped_item");
const TAKE_ACTION = requireTakeAction();

const VISUAL_SOURCES: Readonly<Partial<Record<DropVisualId, DropVisualSource>>> = Object.freeze({
  small_can: visual(createCanGeometry(), [0.37, 0.39, 0.36, 1], 0.11, 0.18, 0.12),
  water_bottle: visual(createBottleGeometry(), [0.34, 0.43, 0.42, 1], 0.16, 0.2, 0.12),
  cloth_bundle: visual(createClothBundleGeometry(), [0.39, 0.36, 0.3, 1], 0.08, 0.24, 0.2),
  scrap_bundle: visual(createScrapBundleGeometry(), [0.33, 0.34, 0.32, 1], 0.1, 0.27, 0.22),
  battery_bundle: visual(createBatteryBundleGeometry(), [0.31, 0.35, 0.3, 1], 0.09, 0.2, 0.15),
  small_tool: visual(createToolGeometry(), [0.31, 0.32, 0.29, 1], 0.055, 0.25, 0.18),
  pickup_bundle: visual(createPickupBundleGeometry(), [0.36, 0.35, 0.31, 1], 0.095, 0.25, 0.2),
});

/**
 * Presentation-only dynamic item layer. Server coordinates are rendered as
 * grounded, non-colliding instances; this class never mutates item state.
 */
export class WorldDropWorld {
  private readonly visuals = new Map<DropVisualId, LoadedDropVisual>();
  private sourceDrops: ReadonlyMap<string, DroppedItemEntity> | undefined;
  private buckets = new Map<string, readonly PreparedDrop[]>();
  private entityCount = 0;
  private visibleCount = 0;
  private drawCalls = 0;
  private lastQueryBuckets = 0;
  private lastQueryEntityChecks = 0;
  private lastQueryTruncated = false;
  private readonly candidateScratch: InteractionCandidate[] = [];
  private disposed = false;

  constructor(private readonly renderer: Renderer) {
    for (const [id, source] of Object.entries(VISUAL_SOURCES) as [DropVisualId, DropVisualSource][]) {
      const mesh = renderer.createMesh(source.geometry);
      // Lightweight unit-test renderers may intentionally omit telemetry.
      if (renderer.metrics) renderer.metrics.itemBufferBytes += mesh.bufferBytes;
      this.visuals.set(id, {
        ...source,
        mesh,
        batch: new ReusableInstanceBatch(),
      });
    }
    void this.loadFlashlight();
  }

  private async loadFlashlight(): Promise<void> {
    try {
      const asset = await loadStaticGlb("/assets/items/flashlight_basic.glb?v=physical-carry-v1");
      if (this.disposed || asset.id !== "flashlight_basic" || asset.primitives.length !== 1) return;
      const primitive = asset.primitives[0]!;
      const source = visual(primitive.geometry, primitive.material.baseColor, asset.boundsCenterY, asset.boundsRadius, 0.15);
      const mesh = this.renderer.createMesh(source.geometry);
      if (this.renderer.metrics) this.renderer.metrics.itemBufferBytes += mesh.bufferBytes;
      this.visuals.set("flashlight_basic", { ...source, mesh, batch: new ReusableInstanceBatch() });
    } catch { /* Existing small-tool silhouette stays available during an asset request failure. */ }
  }

  setSnapshot(snapshot: Pick<ClientInventorySnapshot, "worldDrops">): boolean {
    return this.setDrops(snapshot.worldDrops);
  }

  setDrops(drops: ReadonlyMap<string, DroppedItemEntity>): boolean {
    if (this.disposed || drops === this.sourceDrops) return false;
    this.sourceDrops = drops;
    const mutableBuckets = new Map<string, PreparedDrop[]>();
    let count = 0;
    for (const entity of drops.values()) {
      if (!hasFinitePosition(entity)) continue;
      const definition = getItemDefinition(entity.stack.definitionId);
      const visualId = definition?.worldAssetId ?? "pickup_bundle";
      const prepared: PreparedDrop = Object.freeze({
        entity,
        visualId,
        x: entity.x,
        y: entity.y,
        z: entity.z,
        yaw: entity.yaw,
        scale: 1,
        tint: deterministicTint(entity.entityId),
        chunkX: cellCoordinate(entity.x),
        chunkZ: cellCoordinate(entity.z),
      });
      const key = cellKey(prepared.chunkX, prepared.chunkZ);
      const bucket = mutableBuckets.get(key);
      if (bucket) bucket.push(prepared);
      else mutableBuckets.set(key, [prepared]);
      count += 1;
    }
    this.buckets = new Map([...mutableBuckets].map(([key, bucket]) => [
      key,
      Object.freeze(bucket.sort((left, right) => left.entity.entityId.localeCompare(right.entity.entityId))),
    ]));
    this.entityCount = count;
    if (this.renderer.metrics) this.renderer.metrics.totalItemEntities = count;
    return true;
  }

  render(camera: Camera): void {
    if (this.disposed) return;
    this.visibleCount = 0;
    this.drawCalls = 0;
    for (const visual of this.visuals.values()) visual.batch.reset();
    for (const bucket of this.buckets.values()) {
      for (const drop of bucket) {
        const visual = this.visuals.get(drop.visualId) ?? this.visuals.get("small_tool")!;
        if (!camera.isSphereVisible(
          drop.x,
          drop.y + visual.centerY,
          drop.z,
          visual.boundsRadius,
        )) continue;
        visual.batch.pushUpright(drop, visual.color, 0.08, 0.62);
        this.visibleCount += 1;
      }
    }
    for (const visual of this.visuals.values()) {
      if (visual.batch.count === 0) continue;
      this.renderer.drawStaticInstanced(
        visual.mesh,
        visual.batch.data,
        visual.batch.count,
        MATERIAL,
        "item",
      );
      this.drawCalls += 1;
    }
    if (this.renderer.metrics) this.renderer.metrics.visibleItemEntities = this.visibleCount;
  }

  /** Returns a reused array populated only from nearby spatial buckets. */
  interactionCandidatesAt(
    origin: readonly [x: number, y: number, z: number],
    requestedRadius = WORLD_DROP_QUERY_RADIUS,
  ): readonly InteractionCandidate[] {
    const candidates = this.candidateScratch;
    candidates.length = 0;
    this.lastQueryBuckets = 0;
    this.lastQueryEntityChecks = 0;
    this.lastQueryTruncated = false;
    if (this.disposed || !hasFiniteOrigin(origin)) return candidates;

    const radius = Math.min(
      WORLD_DROP_QUERY_RADIUS_MAXIMUM,
      Number.isFinite(requestedRadius) && requestedRadius > 0 ? requestedRadius : WORLD_DROP_QUERY_RADIUS,
    );
    const minimumX = cellCoordinate(origin[0] - radius);
    const maximumX = cellCoordinate(origin[0] + radius);
    const minimumZ = cellCoordinate(origin[2] - radius);
    const maximumZ = cellCoordinate(origin[2] + radius);

    outer: for (let cellZ = minimumZ; cellZ <= maximumZ; cellZ += 1) {
      for (let cellX = minimumX; cellX <= maximumX; cellX += 1) {
        const bucket = this.buckets.get(cellKey(cellX, cellZ));
        if (!bucket) continue;
        this.lastQueryBuckets += 1;
        for (const drop of bucket) {
          if (this.lastQueryEntityChecks >= WORLD_DROP_QUERY_CHECK_LIMIT) {
            this.lastQueryTruncated = true;
            break outer;
          }
          this.lastQueryEntityChecks += 1;
          const visual = this.visuals.get(drop.visualId) ?? this.visuals.get("small_tool")!;
          const anchorY = drop.y + visual.centerY;
          if (Math.hypot(drop.x - origin[0], anchorY - origin[1], drop.z - origin[2]) > radius) continue;
          const definition = getItemDefinition(drop.entity.stack.definitionId);
          const name = definition?.name ?? "DROPPED ITEM";
          const quantity = drop.entity.stack.quantity;
          candidates.push({
            target: Object.freeze({ kind: "DROPPED_ITEM", id: drop.entity.entityId }),
            objectType: "dropped_item",
            profileId: "dropped_item",
            displayName: quantity > 1 ? `${name} x${quantity}` : name,
            actionId: TAKE_ACTION.id,
            actionLabel: TAKE_ACTION.label,
            durationMs: TAKE_ACTION.durationMs,
            interruptible: TAKE_ACTION.interruptible,
            anchorX: drop.x,
            anchorY,
            anchorZ: drop.z,
            interactionRadius: visual.interactionRadius,
            maxRange: TAKE_ACTION.maxRange,
            priority: TAKE_ACTION.priority,
            objectState: "AVAILABLE",
            revision: drop.entity.revision,
            lineOfSight: true,
            available: true,
          });
        }
      }
    }
    return candidates;
  }

  snapshot(): WorldDropWorldSnapshot {
    return {
      entityCount: this.entityCount,
      visibleCount: this.visibleCount,
      drawCalls: this.drawCalls,
      bucketCount: this.buckets.size,
      lastQueryBuckets: this.lastQueryBuckets,
      lastQueryEntityChecks: this.lastQueryEntityChecks,
      lastQueryTruncated: this.lastQueryTruncated,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const visual of this.visuals.values()) this.renderer.destroyMesh(visual.mesh);
    this.visuals.clear();
    this.buckets.clear();
    this.sourceDrops = undefined;
    this.entityCount = 0;
    this.visibleCount = 0;
    this.drawCalls = 0;
    if (this.renderer.metrics) {
      this.renderer.metrics.totalItemEntities = 0;
      this.renderer.metrics.itemBufferBytes = 0;
    }
  }
}

function visual(
  geometry: MeshGeometry,
  color: readonly [number, number, number, number],
  centerY: number,
  boundsRadius: number,
  interactionRadius: number,
): DropVisualSource {
  return Object.freeze({ geometry, color: Object.freeze(color), centerY, boundsRadius, interactionRadius });
}

function requireTakeAction(): (typeof DROPPED_ITEM_PROFILE.actions)[number] {
  const action = DROPPED_ITEM_PROFILE.actions.find((candidate) => candidate.id === "take");
  if (!action) throw new Error("Dropped-item TAKE interaction profile is missing");
  return action;
}

function cellCoordinate(value: number): number {
  return Math.floor(value / WORLD_DROP_CELL_SIZE);
}

function cellKey(x: number, z: number): string {
  return `${x}:${z}`;
}

function hasFinitePosition(entity: DroppedItemEntity): boolean {
  return Number.isFinite(entity.x)
    && Number.isFinite(entity.y)
    && Number.isFinite(entity.z)
    && Number.isFinite(entity.yaw);
}

function hasFiniteOrigin(origin: readonly number[]): boolean {
  return Number.isFinite(origin[0]) && Number.isFinite(origin[1]) && Number.isFinite(origin[2]);
}

function deterministicTint(id: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < id.length; index += 1) {
    hash ^= id.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619) >>> 0;
  }
  return ((hash % 101) / 100 - 0.5) * 0.05;
}

interface MutableGeometry {
  readonly positions: number[];
  readonly normals: number[];
  readonly indices: number[];
}

function geometry(build: (mesh: MutableGeometry) => void): MeshGeometry {
  const mesh: MutableGeometry = { positions: [], normals: [], indices: [] };
  build(mesh);
  return Object.freeze({
    positions: new Float32Array(mesh.positions),
    normals: new Float32Array(mesh.normals),
    indices: new Uint16Array(mesh.indices),
  });
}

function createCanGeometry(): MeshGeometry {
  return geometry((mesh) => appendCylinder(mesh, 0, 0, 0, 0.105, 0.22, 8));
}

function createBottleGeometry(): MeshGeometry {
  return geometry((mesh) => {
    appendCylinder(mesh, 0, 0, 0, 0.085, 0.23, 8);
    appendCylinder(mesh, 0, 0.23, 0, 0.045, 0.075, 8);
    appendCylinder(mesh, 0, 0.305, 0, 0.05, 0.025, 8);
  });
}

function createClothBundleGeometry(): MeshGeometry {
  return geometry((mesh) => {
    appendBox(mesh, [0, 0.065, 0], [0.36, 0.13, 0.27], 0.08);
    appendBox(mesh, [0.03, 0.14, -0.01], [0.29, 0.07, 0.23], -0.05);
  });
}

function createScrapBundleGeometry(): MeshGeometry {
  return geometry((mesh) => {
    appendBox(mesh, [-0.05, 0.045, 0], [0.36, 0.09, 0.14], 0.24);
    appendBox(mesh, [0.06, 0.095, -0.02], [0.28, 0.07, 0.12], -0.48);
    appendBox(mesh, [-0.02, 0.14, 0.035], [0.21, 0.055, 0.09], 0.68);
  });
}

function createBatteryBundleGeometry(): MeshGeometry {
  return geometry((mesh) => {
    appendCylinder(mesh, -0.055, 0, 0, 0.038, 0.18, 6);
    appendCylinder(mesh, 0.055, 0, 0, 0.038, 0.18, 6);
    appendBox(mesh, [0, 0.085, 0], [0.19, 0.045, 0.09], 0);
  });
}

function createToolGeometry(): MeshGeometry {
  return geometry((mesh) => {
    appendBox(mesh, [-0.07, 0.045, 0], [0.22, 0.09, 0.11], 0.18);
    appendBox(mesh, [0.12, 0.04, 0], [0.24, 0.045, 0.045], 0.18);
  });
}

function createPickupBundleGeometry(): MeshGeometry {
  return geometry((mesh) => {
    appendBox(mesh, [0, 0.075, 0], [0.31, 0.15, 0.25], 0.1);
    appendBox(mesh, [-0.03, 0.17, 0.015], [0.2, 0.055, 0.16], -0.14);
  });
}

function appendBox(
  mesh: MutableGeometry,
  center: readonly [number, number, number],
  size: readonly [number, number, number],
  yaw: number,
): void {
  const halfX = size[0] * 0.5;
  const halfY = size[1] * 0.5;
  const halfZ = size[2] * 0.5;
  const cosine = Math.cos(yaw);
  const sine = Math.sin(yaw);
  const points = [
    [-halfX, -halfY, -halfZ], [halfX, -halfY, -halfZ], [halfX, halfY, -halfZ], [-halfX, halfY, -halfZ],
    [-halfX, -halfY, halfZ], [halfX, -halfY, halfZ], [halfX, halfY, halfZ], [-halfX, halfY, halfZ],
  ].map(([x, y, z]) => [
    center[0] + x! * cosine + z! * sine,
    center[1] + y!,
    center[2] - x! * sine + z! * cosine,
  ] as const);
  const faces = [
    [1, 0, 3], [1, 3, 2], [4, 5, 6], [4, 6, 7],
    [5, 1, 2], [5, 2, 6], [0, 4, 7], [0, 7, 3],
    [7, 6, 2], [7, 2, 3], [0, 1, 5], [0, 5, 4],
  ] as const;
  for (const [a, b, c] of faces) appendTriangle(mesh, points[a]!, points[b]!, points[c]!);
}

function appendCylinder(
  mesh: MutableGeometry,
  centerX: number,
  bottomY: number,
  centerZ: number,
  radius: number,
  height: number,
  sides: number,
): void {
  const bottomCenter = [centerX, bottomY, centerZ] as const;
  const topCenter = [centerX, bottomY + height, centerZ] as const;
  for (let side = 0; side < sides; side += 1) {
    const next = (side + 1) % sides;
    const angle = side / sides * Math.PI * 2;
    const nextAngle = next / sides * Math.PI * 2;
    const bottom = [centerX + Math.sin(angle) * radius, bottomY, centerZ + Math.cos(angle) * radius] as const;
    const bottomNext = [centerX + Math.sin(nextAngle) * radius, bottomY, centerZ + Math.cos(nextAngle) * radius] as const;
    const top = [bottom[0], bottomY + height, bottom[2]] as const;
    const topNext = [bottomNext[0], bottomY + height, bottomNext[2]] as const;
    appendTriangle(mesh, bottom, top, topNext);
    appendTriangle(mesh, bottom, topNext, bottomNext);
    appendTriangle(mesh, bottomCenter, bottomNext, bottom);
    appendTriangle(mesh, topCenter, top, topNext);
  }
}

function appendTriangle(
  mesh: MutableGeometry,
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  c: readonly [number, number, number],
): void {
  const abX = b[0] - a[0];
  const abY = b[1] - a[1];
  const abZ = b[2] - a[2];
  const acX = c[0] - a[0];
  const acY = c[1] - a[1];
  const acZ = c[2] - a[2];
  let normalX = abY * acZ - abZ * acY;
  let normalY = abZ * acX - abX * acZ;
  let normalZ = abX * acY - abY * acX;
  const length = Math.hypot(normalX, normalY, normalZ) || 1;
  normalX /= length;
  normalY /= length;
  normalZ /= length;
  const offset = mesh.positions.length / 3;
  mesh.positions.push(...a, ...b, ...c);
  for (let index = 0; index < 3; index += 1) mesh.normals.push(normalX, normalY, normalZ);
  mesh.indices.push(offset, offset + 1, offset + 2);
}
